import { useState } from "react";
import { importFromSellvia, scrapeSellviaBlog, bulkImportFromSellvia } from "@/lib/api";
import { X, MagnifyingGlass, CircleNotch, LinkSimple, Newspaper, CheckSquare, Square, Sparkle, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * SellviaImporter — modal with two tabs:
 *   • Single URL: scrape one product page from sellviacatalog.com
 *   • Blog Scrape: crawl a sellvia.com/blog post → preview all product links → bulk-import selected
 */
const _selKey = (p) => p.url || `cand:${p.name}`;

export default function SellviaImporter({ onClose, onDone }) {
  const [tab, setTab] = useState("single"); // single | blog
  const [markup, setMarkup] = useState(100);
  const [autoMarketing, setAutoMarketing] = useState(false);

  // Single import state
  const [singleUrl, setSingleUrl] = useState("");
  const [singleLoading, setSingleLoading] = useState(false);

  // Blog import state
  const [blogUrl, setBlogUrl] = useState("");
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogResults, setBlogResults] = useState(null); // { products: [...] }
  const [selected, setSelected] = useState(new Set());
  const [bulkImporting, setBulkImporting] = useState(false);

  const handleSingleImport = async () => {
    if (!singleUrl.trim()) { toast.error("Paste a Sellvia catalog URL"); return; }
    setSingleLoading(true);
    try {
      const r = await importFromSellvia(singleUrl.trim(), Number(markup), autoMarketing);
      toast.success(`Imported: ${r.scraped?.name} — retail $${r.scraped?.your_retail_price}`);
      setSingleUrl("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setSingleLoading(false);
    }
  };

  const handleBlogScrape = async () => {
    if (!blogUrl.trim()) { toast.error("Paste a Sellvia blog URL"); return; }
    setBlogLoading(true);
    setBlogResults(null);
    setSelected(new Set());
    try {
      const r = await scrapeSellviaBlog(blogUrl.trim());
      if ((r.products_scraped || 0) === 0) {
        toast.warning(r.hint || "No products found on that blog post");
      } else {
        toast.success(`Found ${r.products_scraped} product${r.products_scraped === 1 ? "" : "s"}`);
      }
      setBlogResults(r);
      // Pre-select all products that scraped successfully; for catalog-links mode require price>0,
      // for candidates mode select all (price is 0 by design).
      const preselect = new Set(
        (r.products || [])
          .filter(p => !p.error && (p.candidate || p.wholesale_price > 0))
          .map(p => _selKey(p))
      );
      setSelected(preselect);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Blog scrape failed");
    } finally {
      setBlogLoading(false);
    }
  };

  const toggleSelect = (p) => {
    const k = _selKey(p);
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const selectAll = () => {
    const all = new Set((blogResults?.products || []).filter(p => !p.error).map(p => _selKey(p)));
    setSelected(all);
  };
  const selectNone = () => setSelected(new Set());

  const handleBulkImport = async () => {
    const products = (blogResults?.products || []).filter(p => selected.has(_selKey(p)));
    if (products.length === 0) { toast.error("Select at least one product"); return; }
    // Split into catalog URLs vs manual candidates
    const product_urls = products.filter(p => p.url && !p.candidate).map(p => p.url);
    const candidates = products.filter(p => p.candidate).map(p => ({
      name: p.name,
      image_url: p.image_url,
      source_url: blogResults.blog_url,
    }));
    setBulkImporting(true);
    try {
      const r = await bulkImportFromSellvia(product_urls, Number(markup), autoMarketing, candidates);
      toast.success(`Imported ${r.imported_count} products${r.error_count ? ` (${r.error_count} errors)` : ""}`);
      if (r.error_count > 0) console.warn("Bulk import errors:", r.errors);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-arena-surface border border-arena-border w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="sellvia-importer-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-arena-border sticky top-0 bg-arena-surface z-10">
          <div>
            <div className="overline text-arena-primary">// sellvia importer</div>
            <h2 className="font-heading text-xl font-bold mt-1">IMPORT FROM SELLVIA</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="close-importer"
            className="text-arena-muted hover:text-arena-danger"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-arena-border">
          <button
            onClick={() => setTab("single")}
            data-testid="tab-single"
            className={`flex-1 py-3 px-4 text-xs tracking-widest uppercase font-bold flex items-center justify-center gap-2 transition-colors ${tab === "single" ? "bg-arena-primary text-black" : "text-arena-muted hover:text-arena-primary"}`}
          >
            <LinkSimple size={14} weight="bold" />
            Single Product URL
          </button>
          <button
            onClick={() => setTab("blog")}
            data-testid="tab-blog"
            className={`flex-1 py-3 px-4 text-xs tracking-widest uppercase font-bold flex items-center justify-center gap-2 transition-colors ${tab === "blog" ? "bg-arena-primary text-black" : "text-arena-muted hover:text-arena-primary"}`}
          >
            <Newspaper size={14} weight="bold" />
            Blog Bulk Scrape
          </button>
        </div>

        {/* Shared controls */}
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-arena-border">
          <div>
            <label className="overline block mb-1">Markup % (over wholesale)</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={500}
                step={10}
                value={markup}
                onChange={(e) => setMarkup(e.target.value)}
                data-testid="markup-slider"
                className="flex-1 accent-arena-primary"
              />
              <input
                type="number"
                value={markup}
                onChange={(e) => setMarkup(e.target.value)}
                data-testid="markup-input"
                className="w-20 bg-arena-bg border border-arena-border px-2 py-1 text-sm text-center focus:border-arena-primary outline-none"
              />
              <span className="text-xs text-arena-muted">%</span>
            </div>
            <p className="text-[10px] text-arena-muted mt-1">
              e.g. wholesale $10 + 100% markup = <span className="text-arena-primary">$20 retail</span>
            </p>
          </div>
          <div className="flex flex-col justify-center">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-marketing-toggle">
              <input
                type="checkbox"
                checked={autoMarketing}
                onChange={(e) => setAutoMarketing(e.target.checked)}
                className="w-4 h-4 accent-arena-primary"
              />
              <Sparkle size={14} weight="bold" className="text-arena-primary" />
              <span className="text-xs tracking-widest uppercase font-bold">
                Auto-Marketing (AI kit + ad copy)
              </span>
            </label>
            <p className="text-[10px] text-arena-muted mt-1 ml-6">
              Fires in the background after import. Takes ~30-60s per product.
            </p>
          </div>
        </div>

        {/* Single URL tab */}
        {tab === "single" && (
          <div className="p-5">
            <label className="overline block mb-2">Sellvia Product URL</label>
            <div className="flex items-stretch gap-2">
              <input
                type="url"
                value={singleUrl}
                onChange={(e) => setSingleUrl(e.target.value)}
                placeholder="https://sellviacatalog.com/product/12345"
                data-testid="single-url-input"
                className="flex-1 bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none font-mono"
                onKeyDown={(e) => e.key === "Enter" && handleSingleImport()}
              />
              <button
                onClick={handleSingleImport}
                disabled={singleLoading}
                data-testid="single-import-btn"
                className="bg-arena-primary text-black px-5 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover disabled:opacity-50 flex items-center gap-2"
              >
                {singleLoading ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Storefront size={14} weight="bold" />}
                Import
              </button>
            </div>
            <p className="text-[10px] text-arena-muted mt-2">
              Paste the URL from <span className="text-arena-primary">sellviacatalog.com/product/&lt;id&gt;</span>.
              We&apos;ll scrape name, description, image, and wholesale price — then apply your markup.
              Product starts as <span className="text-arena-warning">inactive</span> until you add a delivery URL.
            </p>
          </div>
        )}

        {/* Blog Scrape tab */}
        {tab === "blog" && (
          <div className="p-5">
            <label className="overline block mb-2">Sellvia Blog URL (winning product roundups work best)</label>
            <div className="flex items-stretch gap-2">
              <input
                type="url"
                value={blogUrl}
                onChange={(e) => setBlogUrl(e.target.value)}
                placeholder="https://sellvia.com/blog/best-dropshipping-products-to-sell-now-dteam/"
                data-testid="blog-url-input"
                className="flex-1 bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none font-mono"
                onKeyDown={(e) => e.key === "Enter" && handleBlogScrape()}
              />
              <button
                onClick={handleBlogScrape}
                disabled={blogLoading}
                data-testid="blog-scrape-btn"
                className="bg-arena-primary text-black px-5 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover disabled:opacity-50 flex items-center gap-2"
              >
                {blogLoading ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <MagnifyingGlass size={14} weight="bold" />}
                Scan Blog
              </button>
            </div>

            {/* Suggestions */}
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-[10px] text-arena-muted">Try:</span>
              {[
                { label: "Weekly best products", url: "https://sellvia.com/blog/best-dropshipping-products-to-sell-now-dteam/" },
                { label: "Best of 2026", url: "https://sellvia.com/blog/best-dropshipping-products-this-year/" },
                { label: "Trending picks", url: "https://sellvia.com/blog/trending-dropshipping-products-dteam/" },
              ].map(s => (
                <button
                  key={s.label}
                  onClick={() => setBlogUrl(s.url)}
                  className="text-[10px] border border-arena-border px-2 py-1 hover:border-arena-primary hover:text-arena-primary"
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Results */}
            {blogResults && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-arena-muted">
                    <span className="text-arena-primary font-bold">{blogResults.products_scraped || 0}</span> product{blogResults.products_scraped === 1 ? "" : "s"} scraped
                    {" • "}
                    <span className="text-arena-primary font-bold">{selected.size}</span> selected
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAll}
                      data-testid="select-all-btn"
                      className="text-[10px] tracking-widest uppercase border border-arena-border px-2 py-1 hover:border-arena-primary hover:text-arena-primary"
                    >
                      Select All
                    </button>
                    <button
                      onClick={selectNone}
                      data-testid="select-none-btn"
                      className="text-[10px] tracking-widest uppercase border border-arena-border px-2 py-1 hover:border-arena-primary hover:text-arena-primary"
                    >
                      Select None
                    </button>
                  </div>
                </div>

                {blogResults.mode === "candidates" && (
                  <div className="mb-3 border border-arena-warning/40 bg-arena-warning/10 p-3 text-[11px] text-arena-warning">
                    <span className="font-bold">HEADS UP:</span> {blogResults.hint || "Candidate mode — products imported as drafts."}
                  </div>
                )}

                {(blogResults.products || []).length === 0 ? (
                  <div className="border border-arena-border bg-arena-bg p-8 text-center text-arena-muted text-sm">
                    {blogResults.hint || "No products found on this blog post."}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto pr-1">
                    {(blogResults.products || []).map((p) => {
                      const k = _selKey(p);
                      const isSelected = selected.has(k);
                      const hasError = !!p.error;
                      const isCandidate = !!p.candidate;
                      const retail = Number(p.wholesale_price || 0) * (1 + Number(markup) / 100);
                      return (
                        <label
                          key={k}
                          data-testid={`blog-product-${p.sku || (p.url || p.name).split('/').pop()}`}
                          className={`relative border cursor-pointer transition-all ${hasError ? "border-arena-danger/40 opacity-60" : isSelected ? "border-arena-primary shadow-[0_0_0_1px_#ccff00]" : "border-arena-border hover:border-arena-primary/50"}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={hasError}
                            onChange={() => toggleSelect(p)}
                            className="sr-only"
                          />
                          <div className="absolute top-2 left-2 z-10">
                            {isSelected ? (
                              <CheckSquare size={18} weight="fill" className="text-arena-primary" />
                            ) : (
                              <Square size={18} weight="regular" className="text-arena-muted" />
                            )}
                          </div>
                          {isCandidate && (
                            <div className="absolute top-2 right-2 z-10 bg-arena-warning text-black text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5">
                              draft
                            </div>
                          )}
                          {p.image_url ? (
                            <div className="aspect-square bg-black overflow-hidden">
                              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                          ) : (
                            <div className="aspect-square bg-arena-bg flex items-center justify-center text-arena-muted text-[10px]">
                              no image
                            </div>
                          )}
                          <div className="p-2">
                            <div className="text-[11px] font-bold truncate" title={p.name}>{p.name || "—"}</div>
                            {hasError ? (
                              <div className="text-[9px] text-arena-danger mt-1 truncate" title={p.error}>err: {p.error}</div>
                            ) : isCandidate ? (
                              <div className="text-[10px] text-arena-muted mt-1">Set price later</div>
                            ) : (
                              <div className="flex items-baseline gap-1 mt-1">
                                <span className="text-[10px] text-arena-muted line-through">${Number(p.wholesale_price).toFixed(2)}</span>
                                <span className="text-sm font-heading font-bold text-arena-primary">${retail.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}

                {selected.size > 0 && (
                  <div className="mt-4 sticky bottom-0 bg-arena-surface pt-3 border-t border-arena-border flex items-center justify-between">
                    <div className="text-xs text-arena-muted">
                      Ready to import <span className="text-arena-primary font-bold">{selected.size}</span> product{selected.size === 1 ? "" : "s"} @ <span className="text-arena-primary font-bold">{markup}%</span> markup
                      {autoMarketing && <span className="text-arena-primary"> + auto-marketing</span>}
                    </div>
                    <button
                      onClick={handleBulkImport}
                      disabled={bulkImporting}
                      data-testid="bulk-import-btn"
                      className="bg-arena-primary text-black px-5 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover disabled:opacity-50 flex items-center gap-2"
                    >
                      {bulkImporting ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Storefront size={14} weight="bold" />}
                      Import {selected.size} Selected
                    </button>
                  </div>
                )}
              </div>
            )}

            {!blogResults && !blogLoading && (
              <p className="text-[10px] text-arena-muted mt-3">
                Paste any Sellvia blog URL that lists winning products.
                We&apos;ll find all catalog links, scrape each product, then let you pick which ones to bulk-import into your store.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
